import React, { useState, useEffect } from 'react';
import {
  Minus,
  Plus,
  ChefHat,
  MessageCircle,
  Utensils,
  ShoppingCart,
  X,
  Check
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const PizzaComponent = () => {
  const dispatch = useDispatch();

  const [selections, setSelections] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [chefComment, setChefComment] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [pizzaItems, setPizzaItems] = useState([]);

  useEffect(() => {
    const docRef = doc(db, 'menuCategories', 'Pizza');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setPizzaItems(docSnapshot.data().items || []);
        } else {
          setPizzaItems([]);
        }
      },
      (error) => {
        console.error("Error fetching Pastry data:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Update topping quantity using plus/minus controls remains unchanged
  const handleToppingQuantityChange = (itemId, toppingName, delta) => {
    setSelections(prev => {
      const currentTopping = prev[itemId]?.extras?.toppings?.find(t => t.name === toppingName);
      const currentQuantity = currentTopping?.quantity || 1;
      const newQuantity = Math.max(1, currentQuantity + delta);
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          extras: {
            ...prev[itemId]?.extras,
            toppings: (prev[itemId]?.extras?.toppings || []).map(topping =>
              topping.name === toppingName ? { ...topping, quantity: newQuantity } : topping
            )
          }
        }
      };
    });
  };

  // Opens the Customize Toppings modal for the selected pizza item
  const handleOpenModal = (itemId) => {
    const item = pizzaItems.find(item => item.id === itemId);
    setSelectedItem(item);
    setSelectedItemId(itemId);
    setIsModalOpen(true);
  };

  // Closes the Customize Toppings modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  };

  // Handler for changing the selected pizza size (dropdown remains unchanged)
  const handleSizeChange = (itemId, value) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], type: value }
    }));
  };

  // Handler for toggling a topping selection on/off
  const handleToppingChange = (itemId, topping, isChecked) => {
    const toppingInfo = selectedItem.extras.toppings.find(t => t.name === topping.name);
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        extras: {
          ...prev[itemId]?.extras || {},
          toppings: isChecked
            ? [...(prev[itemId]?.extras?.toppings || []), { ...toppingInfo, quantity: 1 }]
            : (prev[itemId]?.extras?.toppings || []).filter(t => t.name !== topping.name)
        }
      }
    }));
  };

  // Add-to-cart handler calculates final price using the actual size price plus toppings cost
  const handleAddToCart = (item) => {
    const itemSelections = selections[item.id] || {};
    // Use default size "Small" if no size was selected
    const selectedSize = itemSelections.type || 'Small';
    if (!itemSelections.type) {
      setSelections(prev => ({
        ...prev,
        [item.id]: { ...prev[item.id], type: 'Small' }
      }));
    }
    const sizePrice = item.extras.sizes.find(s => s.name === selectedSize)?.price || item.price;
    const toppingsCost = (itemSelections.extras?.toppings || []).reduce(
      (sum, topping) => sum + (topping.price * (topping.quantity || 1)),
      0
    );
    const finalPrice = sizePrice + toppingsCost;
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name,
      price: finalPrice,
      quantity: itemSelections.quantity || 1,
      extras: itemSelections.extras || {},
      type: selectedSize,
      chefComment: chefComment
    };

    dispatch(addToCart(cartItem));
    toast.success(`${item.name} added to cart!`, {
      position: "top-right",
      autoClose: 3000
    });
  };

  // Handles overall quantity change of the pizza item.
  const handleQuantityChange = (itemId, delta) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, (prev[itemId]?.quantity || 1) + delta)
      }
    }));
  };

  // Renders each pizza card. Interactive controls are only rendered when the item is available.
  const renderMenuItem = (item) => {
    const itemSelections = selections[item.id] || {};
    const quantity = itemSelections.quantity || 1;
    // Use the actual price from the selected size (or default to "Small")
    const displayPrice = item.extras.sizes.find(s => s.name === (itemSelections.type || 'Small'))?.price || item.price;

    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 relative">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          {/* Overlay for unavailable pizza items */}
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
              <span className="text-red-600 font-bold text-xl">Not Available</span>
            </div>
          )}
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-600 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{displayPrice.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
            <p className="text-gray-400 mt-1 text-sm">{item.description}</p>
          </div>

          {item.isAvailable && (
            <>
              <div className="space-y-3">
                <div className="relative">
                  <select
                    className="w-full appearance-none bg-black border border-gray-700 text-gray-300 py-2 px-3 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={itemSelections.type || 'Small'}
                    onChange={(e) => handleSizeChange(item.id, e.target.value)}
                  >
                    {item.extras.sizes.map((size, idx) => (
                      <option key={idx} value={size.name}>{size.name}</option>
                    ))}
                  </select>
                  <ChefHat className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                </div>
              </div>

              {/* Extras and Chef Note buttons in a row */}
              <div className="flex flex-row gap-2">
                {item.extras.toppings && item.extras.toppings.length > 0 && (
                  <button
                    onClick={() => handleOpenModal(item.id)}
                    className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Utensils className="w-4 h-4" />
                    <span>Extra Toppings</span>
                  </button>
                )}
                <button
                  onClick={() => setIsCommentModalOpen(true)}
                  className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Chef Note</span>
                </button>
              </div>

              {/* Quantity and Add to Cart controls arranged in a row */}
              <div className="flex flex-row gap-2 pt-2 items-center">
                <div className="flex items-center gap-2 bg-black rounded-lg p-1">
                  <button
                    onClick={() => handleQuantityChange(item.id, -1)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-medium text-red-500">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(item.id, 1)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => handleAddToCart(item)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>Add to Cart</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-red-500">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pizzaItems.map(item => renderMenuItem(item))}
        </div>
      </div>

      {/* Customize Toppings Modal */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Customize Toppings</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedItem.extras.toppings && selectedItem.extras.toppings.map((topping, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`${selectedItemId}-${topping.name}`}
                      checked={!!(selections[selectedItemId]?.extras?.toppings?.find(t => t.name === topping.name))}
                      onChange={(e) => handleToppingChange(selectedItemId, topping, e.target.checked)}
                      className="w-4 h-4 text-red-500 rounded border-gray-700 focus:ring-red-500 focus:ring-2"
                    />
                    <label htmlFor={`${selectedItemId}-${topping.name}`} className="text-gray-200">
                      {topping.name}
                      <span className="text-sm text-gray-400 block">₦{topping.price.toLocaleString()}</span>
                    </label>
                  </div>

                  {selections[selectedItemId]?.extras?.toppings?.find(t => t.name === topping.name) && (
                    <div className="flex items-center gap-2 bg-black rounded-lg p-1">
                      <button
                        onClick={() => handleToppingQuantityChange(selectedItemId, topping.name, -1)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        disabled={selections[selectedItemId].extras.toppings.find(t => t.name === topping.name).quantity <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium text-red-500">
                        {selections[selectedItemId]?.extras?.toppings?.find(t => t.name === topping.name)?.quantity || 1}
                      </span>
                      <button
                        onClick={() => handleToppingQuantityChange(selectedItemId, topping.name, 1)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Done</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chef Comment Modal */}
      {isCommentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Note for the Chef</h3>
              <button onClick={() => setIsCommentModalOpen(false)} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={chefComment}
              onChange={(e) => setChefComment(e.target.value)}
              placeholder="Any special requests or preferences?"
              className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="px-6 py-2 border border-gray-600 text-gray-200 hover:bg-gray-800 rounded-lg transition-colors focus:ring-2 focus:ring-red-500"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PizzaComponent;
